/**
 * EcadRibbon — section ribbon for the Electronics (Altium/ECAD) view.
 *
 * Deliberately minimal: only the actions wired end-to-end on main — Link /
 * Suggest Links (Trace) and New Version (Review). BOM import is intentionally
 * absent until the ECAD-BOM machinery (EcadBomImportDialog + importEcadBom +
 * MPN/parts) lands from the cross-domain branch; we don't ship a dead button.
 *
 * Same visual language as CadRibbon but a separate component so neither view
 * carries buttons the other needs.
 */
import React, { useState, useEffect } from 'react';
import { Box, Tooltip, IconButton, Divider, Typography } from '@mui/material';
import { AutoFixHigh, ExpandLess, ExpandMore, Link as LinkIcon, NoteAdd } from '@mui/icons-material';

const EXPANDED_HEIGHT = 72;
const COLLAPSED_HEIGHT = 36;

const RibbonButton = ({ label, icon, disabled, disabledTooltip, onClick, collapsed }) => {
    const tip = disabled && disabledTooltip ? disabledTooltip : label;
    const button = (
        <Box
            component="button"
            disabled={disabled}
            onClick={disabled ? undefined : onClick}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: collapsed ? 0 : 0.25,
                minWidth: collapsed ? 32 : 56,
                px: 1,
                py: 0.5,
                background: 'transparent',
                border: 'none',
                borderRadius: 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.4 : 1,
                color: 'text.primary',
                transition: 'background 0.15s',
                '&:hover': disabled ? {} : { background: 'action.hover' },
            }}
        >
            <Box sx={{ display: 'flex', '& svg': { fontSize: collapsed ? 18 : 22 } }}>{icon}</Box>
            {!collapsed && (
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.7rem',
                        lineHeight: 1,
                        textAlign: 'center',
                        maxWidth: 70,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </Typography>
            )}
        </Box>
    );
    return (
        <Tooltip title={tip} placement="bottom">
            <span>{button}</span>
        </Tooltip>
    );
};

const GroupDivider = () => <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />;

const EcadRibbon = ({ selectedFile, onLink, onSuggestLinks, onNewVersion }) => {
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem('eis_ecad_ribbon_collapsed') === '1';
        } catch {
            return false;
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem('eis_ecad_ribbon_collapsed', collapsed ? '1' : '0');
        } catch {
            /* ignore */
        }
    }, [collapsed]);

    const hasTarget = !!selectedFile?.id;
    const needsTarget = 'Select a design first';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                height: collapsed ? COLLAPSED_HEIGHT : EXPANDED_HEIGHT,
                px: 1,
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                overflowX: 'auto',
                transition: 'height 0.15s',
            }}
        >
            {/* Trace */}
            <RibbonButton
                label="Link"
                icon={<LinkIcon />}
                disabled={!hasTarget}
                disabledTooltip={needsTarget}
                onClick={() => onLink?.(selectedFile)}
                collapsed={collapsed}
            />
            <RibbonButton
                label="Suggest Links"
                icon={<AutoFixHigh />}
                disabled={!hasTarget}
                disabledTooltip={needsTarget}
                onClick={() => onSuggestLinks?.(selectedFile)}
                collapsed={collapsed}
            />

            <GroupDivider />

            {/* Review */}
            <RibbonButton
                label="New Version"
                icon={<NoteAdd />}
                disabled={!hasTarget}
                disabledTooltip={needsTarget}
                onClick={() => onNewVersion?.(selectedFile)}
                collapsed={collapsed}
            />

            <Box sx={{ flexGrow: 1 }} />

            <Tooltip title={collapsed ? 'Expand ribbon' : 'Collapse ribbon'} placement="bottom">
                <IconButton size="small" onClick={() => setCollapsed((v) => !v)} sx={{ alignSelf: 'center', mx: 0.5 }}>
                    {collapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default EcadRibbon;
